<template>
  <v-dialog v-model="dialog" max-width="800">
    <v-card>
      <v-card-title class="text-h5">
        Crear Broker Externo
      </v-card-title>
      <v-card-text>
        <v-stepper v-model="step">
          <v-stepper-header class="ma-0 pa-0">
            <v-stepper-step :complete="step > 1" step="1">
              Cliente
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 2" step="2">
              Datos del Broker
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step v-if="hasClient" :complete="step > 3" step="3">
              Datos del Cliente
            </v-stepper-step>
            <v-divider v-if="hasClient"></v-divider>
            <v-stepper-step v-if="hasClient" step="4">
              Confirmación
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- Paso 1: Pregunta si trae cliente -->
            <v-stepper-content class="ma-0 pa-0" step="1">
              <v-card>
                <v-card-text class="">
                  <h3 class="mb-4">¿El broker trae cliente?</h3>
                  <v-radio-group v-model="hasClient" row hide-details>
                    <v-radio label="Sí" :value="true"></v-radio>
                    <v-radio label="No" :value="false"></v-radio>
                  </v-radio-group>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="dialog = false">Cancelar</v-btn>
                  <v-btn color="primary" @click="step = 2">Continuar</v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>

            <!-- Paso 2: Datos del Broker -->
            <v-stepper-content class="ma-0 pa-0" step="2">
              <v-card flat>
                <v-card-text>
                  <h3 class="mb-4">Datos del Broker</h3>
                  <v-text-field
                    v-model="brokerData.name"
                    label="Nombre"
                    :value="lead.contact_lead_name"
                    :rules="[v => !!v || 'El nombre es requerido']"
                    required
                  ></v-text-field>
                  <v-text-field
                    v-model="brokerData.email"
                    label="Correo"
                    :value="lead.contact_lead_email"
                    :rules="[
                      v => !!v || 'El correo es requerido',
                      v => /.+@.+\..+/.test(v) || 'El correo debe ser válido'
                    ]"
                    required
                  ></v-text-field>
                  <v-text-field
                    v-model="brokerData.phone"
                    label="Teléfono"
                    :value="lead.contact_lead_phone"
                    :rules="[v => !!v || 'El teléfono es requerido']"
                    required
                  ></v-text-field>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="step = 1">Atrás</v-btn>
                  <v-btn
                    v-if="hasClient"
                    color="primary"
                    @click="validateBrokerData"
                    :disabled="!isBrokerDataValid"
                  >
                    Continuar
                  </v-btn>
                  <v-btn
                    v-else
                    color="primary"
                    :loading="loading"
                    @click="handleSubmit"
                    :disabled="!isBrokerDataValid"
                  >
                    Finalizar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>

            <!-- Paso 3: Datos del Cliente (solo si hasClient es true) -->
            <v-stepper-content class="ma-0 pa-0" v-if="hasClient" step="3">
              <v-card flat>
                <v-card-text>
                  <h3 class="mb-4">Datos del Cliente</h3>
                  <v-text-field
                    v-model="clientData.name"
                    label="Nombre del Cliente"
                    :rules="[v => !!v || 'El nombre es requerido']"
                    required
                  ></v-text-field>
                  <v-text-field
                    v-model="clientData.email"
                    label="Correo del Cliente"
                    :rules="[
                      v =>
                        !v || /.+@.+\..+/.test(v) || 'El correo debe ser válido'
                    ]"
                  ></v-text-field>
                  <v-text-field
                    v-model="clientData.phone"
                    label="Teléfono del Cliente"
                  ></v-text-field>
                  <v-alert
                    class="ma-0 pa-1"
                    v-if="!hasClientContactInfo"
                    type="warning"
                    outlined
                    dense
                  >
                    Debe proporcionar al menos un dato de contacto (correo o
                    teléfono)
                  </v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="step = 2">Atrás</v-btn>
                  <v-btn
                    color="primary"
                    @click="validateClientData"
                    :disabled="!isClientDataValid"
                  >
                    Continuar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>

            <!-- Paso 4: Confirmación -->
            <v-stepper-content class="ma-0 pa-0" step="4">
              <v-card flat>
                <v-card-text>
                  <h3 class="mb-4">Confirmación de Datos</h3>

                  <v-card outlined class="mb-4">
                    <v-card-title class="subtitle-1"
                      >Datos del Broker</v-card-title
                    >
                    <v-card-text>
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Nombre</v-list-item-title>
                            <v-list-item-subtitle>{{
                              brokerData.name
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Correo</v-list-item-title>
                            <v-list-item-subtitle>{{
                              brokerData.email
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Teléfono</v-list-item-title>
                            <v-list-item-subtitle>{{
                              brokerData.phone
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>

                  <v-card v-if="hasClient" outlined>
                    <v-card-title class="subtitle-1"
                      >Datos del Cliente</v-card-title
                    >
                    <v-card-text>
                      <v-list dense>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Nombre</v-list-item-title>
                            <v-list-item-subtitle>{{
                              clientData.name
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Correo</v-list-item-title>
                            <v-list-item-subtitle>{{
                              clientData.email
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>Teléfono</v-list-item-title>
                            <v-list-item-subtitle>{{
                              clientData.phone
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="step = hasClient ? 3 : 2">Atrás</v-btn>
                  <v-btn
                    color="primary"
                    :loading="loading"
                    @click="handleSubmit"
                  >
                    Confirmar y Crear
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import { convertLeadToExternalBroker } from "@/api/external-broker-api";

export default {
  name: "LeadTransformExternalBroker",
  data() {
    return {
      dialog: false,
      loading: false,
      step: 1,
      hasClient: false,
      brokerData: {
        name: "",
        email: "",
        phone: ""
      },
      clientData: {
        name: "",
        email: "",
        phone: ""
      },
      formErrors: {
        broker: {},
        client: {}
      }
    };
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    lead: {
      type: Object,
      default: null
    }
  },
  computed: {
    ...mapState("leads", ["actualItem"]),
    isBrokerDataValid() {
      return (
        this.brokerData.name &&
        this.brokerData.email &&
        this.brokerData.phone &&
        /.+@.+\..+/.test(this.brokerData.email)
      );
    },
    hasClientContactInfo() {
      return this.clientData.email || this.clientData.phone;
    },
    isClientDataValid() {
      return this.clientData.name && this.hasClientContactInfo;
    }
  },
  watch: {
    showDialog(newVal) {
      this.dialog = newVal;
    },
    dialog(newVal) {
      this.$emit("update:showDialog", newVal);
    },
    lead: {
      immediate: true,
      handler(newVal) {
        this.step = 1;
        if (newVal) {
          this.brokerData = {
            name: newVal.contact_lead_name,
            email: newVal.contact_lead_email,
            phone: newVal.contact_lead_phone
          };
        }
      }
    }
  },
  methods: {
    ...mapMutations("leads", [
      "setLeadDetail",
      "setLeadInList",
      "setLeadInKanban",
      "deleteLeadInList",
      "deleteLeadInKanban"
    ]),
    validateBrokerData() {
      if (this.isBrokerDataValid) {
        if (this.hasClient) {
          this.step = 3;
        } else {
          this.step = 4;
        }
      }
    },
    validateClientData() {
      if (this.isClientDataValid) {
        this.step = 4;
      }
    },
    nextStep() {
      if (this.hasClient) {
        this.step = 3;
      } else {
        this.step = 4;
      }
    },
    async handleSubmit() {
      if (!this.isBrokerDataValid) {
        this.$snotify.error(
          "Por favor complete correctamente los datos del broker"
        );
        this.step = 2;
        return;
      }

      if (this.hasClient && !this.isClientDataValid) {
        this.$snotify.error(
          "Por favor complete correctamente los datos del cliente"
        );
        this.step = 3;
        return;
      }

      try {
        this.loading = true;
        // // Aquí irá la lógica para crear el broker y el lead
        // const broker = await createBrokerExternal({
        //   ...this.brokerData,
        //   type: "external"
        // });

        // if (broker) {
        //   await deleteLead(this.lead._id);
        // }

        // if (this.hasClient) {
        //   await CreateLead(this.clientData);
        // }
        await convertLeadToExternalBroker({
          leadId: this.lead._id,
          brexData: this.brokerData,
          ...(this.hasClient && { leadData: this.clientData })
        });

        this.setNewLead(
          {
            ...this.lead,
            ...this.clientData,
            contact_lead: { ...this.lead.contact_lead, ...this.clientData },
            external_broker: {
              ...this.lead.external_broker,
              ...this.brokerData
            }
          },
          !this.hasClient
        );

        this.$snotify.success("Broker externo creado exitosamente");
        this.dialog = false;
      } catch (error) {
        this.$snotify.error(error);
      } finally {
        this.$emit("refresh");
        this.loading = false;
      }
    },
    getColumnKey(lead) {
      return lead.tracking_phase || lead.operation_phase || lead.phase;
    },
    setNewLead(newLead, deleteLead = true) {
      const columnValue = this.getColumnKey(newLead);
      if (deleteLead) {
        this.deleteLeadInList(newLead);
        this.deleteLeadInKanban({ lead: newLead, columnValue });
      } else {
        this.setLeadDetail(newLead);
        this.setLeadInList(newLead);
        this.setLeadInKanban({ lead: newLead, columnValue });
      }
    }
  }
};
</script>

<style scoped>
.v-stepper {
  box-shadow: none !important;
}
</style>
